import {
  Button,
  FormControl,
  Grid2 as Grid,
  OutlinedInput,
  Select,
  MenuItem,
  ListItemText,
  Box,
  Chip,
  Typography,
  Paper,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  IconButton,
  Popover,
} from "@mui/material";
import {
  Search as SearchIcon,
  Tune as TuneIcon,
  RestartAlt as ResetIcon,
} from "@mui/icons-material";
import useFilter from "./useFilter";
import { useState } from "react";
import { CompanyStampListRequestDto } from "@/dto/CompanyStampDto";
import { SubscriptionProductSelectionDto } from "@/dto/SubscriptionDto";
import { TemplateSelectionDto } from "@/dto/TemplatesDto";

export type FilterProps = {
  values: CompanyStampListRequestDto;
  onChange: (values: CompanyStampListRequestDto) => void;
  onReset: () => void;
  subscriptionProductList: SubscriptionProductSelectionDto[];
  templateList: TemplateSelectionDto[];
};

export const Filter = (props: FilterProps) => {
  const { values, onChange, onReset, subscriptionProductList, templateList } =
    props;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [searchFocused, setSearchFocused] = useState(false);

  const {
    formValues,
    handleSearch,
    handleReset,
    handleComNmChange,
    handleAdmNmChange,
    handleProductIdChange,
    handleTemplateIdChange,
    handleSubscriptionStatChange,
  } = useFilter(values, onChange, onReset);

  const handleOpenFilters = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseFilters = () => {
    setAnchorEl(null);
  };

  const handleApplyFilters = () => {
    handleSearch();
    handleCloseFilters();
  };

  const open = Boolean(anchorEl);
  const id = open ? "filters-popover" : undefined;

  // 구독 상태 표시 문자열 생성
  const getStatusDisplayName = (status: string | undefined) => {
    switch (status) {
      case "PENDING":
        return "대기";
      case "ACTIVE":
        return "활성";
      case "CANCELED":
        return "취소";
      case "EXPIRED":
        return "만료";
      default:
        return null;
    }
  };

  // 활성화된 필터 태그 생성
  const renderFilterTags = () => {
    const tags = [];

    if (formValues.comNm) {
      tags.push(
        <Chip
          key="comNm"
          label={`도장명: ${formValues.comNm}`}
          size="small"
          onDelete={() => handleComNmChange("")}
          sx={{ mr: 1, mb: 1 }}
        />
      );
    }

    if (formValues.admNm) {
      tags.push(
        <Chip
          key="admNm"
          label={`관리자명: ${formValues.admNm}`}
          size="small"
          onDelete={() => handleAdmNmChange("")}
          sx={{ mr: 1, mb: 1 }}
        />
      );
    }

    if (formValues.productId) {
      const product = subscriptionProductList.find(
        (p) => p.productId === formValues.productId
      );
      if (product) {
        tags.push(
          <Chip
            key="product"
            label={`상품: ${product.productNm}`}
            size="small"
            onDelete={() => handleProductIdChange("")}
            sx={{ mr: 1, mb: 1 }}
          />
        );
      }
    }

    if (formValues.templateId) {
      const template = templateList.find(
        (t) => t.templateId === formValues.templateId
      );
      if (template) {
        tags.push(
          <Chip
            key="template"
            label={`템플릿: ${template.templateNm}`}
            size="small"
            onDelete={() => handleTemplateIdChange("")}
            sx={{ mr: 1, mb: 1 }}
          />
        );
      }
    }

    if (formValues.subscriptionStat) {
      tags.push(
        <Chip
          key="status"
          label={`상태: ${getStatusDisplayName(formValues.subscriptionStat)}`}
          size="small"
          onDelete={() => handleSubscriptionStatChange(undefined)}
          sx={{ mr: 1, mb: 1 }}
        />
      );
    }

    return tags.length > 0 ? (
      <Box sx={{ display: "flex", flexWrap: "wrap", mt: 2 }}>
        {tags}
        {tags.length > 0 && (
          <Chip
            label="모두 지우기"
            size="small"
            variant="outlined"
            onClick={handleReset}
            deleteIcon={<ResetIcon />}
            sx={{ mb: 1 }}
          />
        )}
      </Box>
    ) : null;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        mb: 3,
      }}
    >
      {/* 메인 검색바 */}
      <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 12, sm: 8, md: 10 }}>
          <FormControl fullWidth variant="outlined" size="small">
            <OutlinedInput
              placeholder="도장명 또는 관리자명 검색"
              value={formValues.comNm || ""}
              onChange={(event) => {
                handleComNmChange(event.target.value);
              }}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon color={searchFocused ? "primary" : "action"} />
                </InputAdornment>
              }
              sx={{
                transition: "all 0.2s",
                boxShadow: searchFocused ? 1 : 0,
                "&.Mui-focused": {
                  borderColor: "primary.main",
                },
              }}
            />
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, sm: 4, md: 2 }} sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="고급 필터 옵션">
            <IconButton
              color="primary"
              onClick={handleOpenFilters}
              aria-describedby={id}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                flex: 1,
              }}
            >
              <TuneIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            disableElevation
            onClick={handleSearch}
            sx={{ flex: 3 }}
          >
            검색
          </Button>
        </Grid>
      </Grid>

      {/* 선택된 필터 태그 */}
      {renderFilterTags()}

      {/* 고급 필터 팝오버 */}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseFilters}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            width: 320,
            p: 2,
            mt: 1,
          },
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          고급 필터
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            관리자명
          </Typography>
          <FormControl fullWidth variant="outlined" size="small">
            <OutlinedInput
              placeholder="관리자명 입력"
              value={formValues.admNm || ""}
              onChange={(event) => {
                handleAdmNmChange(event.target.value);
              }}
              sx={{ mt: 0.5 }}
            />
          </FormControl>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            상품
          </Typography>
          <FormControl fullWidth variant="outlined" size="small">
            <Select
              value={formValues.productId || ""}
              onChange={(event) => {
                handleProductIdChange(event.target.value);
              }}
              displayEmpty
              sx={{ mt: 0.5 }}
            >
              <MenuItem value="">
                <ListItemText primary="전체" />
              </MenuItem>
              {subscriptionProductList.map((product) => (
                <MenuItem key={product.productId} value={product.productId}>
                  <ListItemText primary={product.productNm} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            템플릿
          </Typography>
          <FormControl fullWidth variant="outlined" size="small">
            <Select
              value={formValues.templateId || ""}
              onChange={(event) => {
                handleTemplateIdChange(event.target.value);
              }}
              displayEmpty
              sx={{ mt: 0.5 }}
            >
              <MenuItem value="">
                <ListItemText primary="전체" />
              </MenuItem>
              {templateList.map((template) => (
                <MenuItem key={template.templateId} value={template.templateId}>
                  <ListItemText primary={template.templateNm} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            구독상태
          </Typography>
          <ToggleButtonGroup
            value={formValues.subscriptionStat || ""}
            exclusive
            onChange={(_, value) =>
              handleSubscriptionStatChange(value || undefined)
            }
            fullWidth
            size="small"
            sx={{ mt: 0.5 }}
          >
            <ToggleButton value="PENDING" aria-label="대기">
              대기
            </ToggleButton>
            <ToggleButton value="ACTIVE" aria-label="활성">
              활성
            </ToggleButton>
            <ToggleButton value="CANCELED" aria-label="취소">
              취소
            </ToggleButton>
            <ToggleButton value="EXPIRED" aria-label="만료">
              만료
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", pt: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleReset}
            startIcon={<ResetIcon />}
          >
            초기화
          </Button>
          <Button
            variant="contained"
            disableElevation
            size="small"
            onClick={handleApplyFilters}
          >
            적용
          </Button>
        </Box>
      </Popover>
    </Paper>
  );
};

export default Filter;
